// lib/currency.ts — EV-418 single source of truth for currency display.
// Consolidates the CURRENCIES map + FALLBACK_RATES that were duplicated across
// MenuPage.tsx, RichMenuView.tsx and EventDetailPage.tsx. Prices are ALWAYS
// stored in VND; conversion is display-only. The user's "home currency"
// preference (EV-418) drives the bracketed equivalent (e.g. "₫500,000 ($20)").

import { dtLocale } from '../utils/dateLocale';
export type CurrencyMeta = { symbol: string; label: string; cc: string; decimals: number };

// Order here = order shown in the selector. VND first (base), then the
// tourist/home currencies. MYR + SGD + JPY are mandatory per the CLAUDE.md
// currency law — do not drop them.
export const CURRENCIES: Record<string, CurrencyMeta> = {
  VND: { symbol: '₫', label: 'Vietnamese Dong', cc: 'vn', decimals: 0 },
  USD: { symbol: '$', label: 'US Dollar', cc: 'us', decimals: 2 },
  EUR: { symbol: '€', label: 'Euro', cc: 'eu', decimals: 2 },
  GBP: { symbol: '£', label: 'British Pound', cc: 'gb', decimals: 2 },
  THB: { symbol: '฿', label: 'Thai Baht', cc: 'th', decimals: 2 },
  RUB: { symbol: '₽', label: 'Russian Ruble', cc: 'ru', decimals: 0 },
  AUD: { symbol: 'A$', label: 'Australian Dollar', cc: 'au', decimals: 2 },
  KRW: { symbol: '₩', label: 'Korean Won', cc: 'kr', decimals: 0 },
  JPY: { symbol: '¥', label: 'Japanese Yen', cc: 'jp', decimals: 0 },
  CNY: { symbol: '¥', label: 'Chinese Yuan', cc: 'cn', decimals: 2 },
  ILS: { symbol: '₪', label: 'Israeli Shekel', cc: 'il', decimals: 2 },
  INR: { symbol: '₹', label: 'Indian Rupee', cc: 'in', decimals: 2 },
  MYR: { symbol: 'RM', label: 'Malaysian Ringgit', cc: 'my', decimals: 2 },
  SGD: { symbol: 'S$', label: 'Singapore Dollar', cc: 'sg', decimals: 2 },
};

export const CURRENCY_CODES = Object.keys(CURRENCIES);

// Fallback rates (1 VND → currency, approximate). Used until the live rate
// fetch resolves, and as a safety net if it fails.
export const FALLBACK_RATES: Record<string, number> = {
  VND: 1, USD: 0.000039, EUR: 0.000036, GBP: 0.000031, THB: 0.0013,
  RUB: 0.0034, AUD: 0.000061, KRW: 0.054, JPY: 0.006, CNY: 0.00028,
  ILS: 0.00014, INR: 0.0033, MYR: 0.00017, SGD: 0.000052,
};

const FLAG_BASE = 'https://flagcdn.com/16x12';
export function flagUrl(cc: string): string {
  return `${FLAG_BASE}/${cc}.png`;
}

// Detect a sensible default currency from the browser locale.
export function detectCurrency(): string {
  const lang = (typeof navigator !== 'undefined' ? navigator.language : 'en' || 'en').toLowerCase();
  const base = lang.split('-')[0];
  const region = lang.includes('-') ? lang.split('-')[1] : '';
  if (lang === 'en-gb') return 'GBP';
  if (lang === 'en-au') return 'AUD';
  if (lang === 'en-ie' || lang === 'en-mt') return 'EUR';
  if (base === 'vi') return 'VND';
  if (base === 'th') return 'THB';
  if (base === 'ru') return 'RUB';
  if (base === 'ko') return 'KRW';
  if (base === 'ja') return 'JPY';
  if (base === 'zh') return 'CNY';
  if (base === 'he' || base === 'iw') return 'ILS';
  if (base === 'hi') return 'INR';
  if (base === 'ms') return 'MYR';
  const euroLangs = ['de','fr','it','es','nl','pt','el','fi','ga','sk','sl','et','lv','lt','mt','lb','hr','cs','pl','hu','ro','bg','sv','da','nb','nn','is','ca','eu','gl'];
  if (euroLangs.includes(base)) return 'EUR';
  const euroRegions = ['de','fr','it','es','nl','pt','at','be','ie','fi','gr','ee','lv','lt','lu','mt','sk','si','cy','hr'];
  if (region && euroRegions.includes(region)) return 'EUR';
  return 'USD';
}

const HOME_CCY_KEY = 'hl_home_currency';

// The user's chosen home/display currency. Priority:
//   1. explicit localStorage pick (set when they choose in /me, or hydrated from
//      auth.users.preferred_currency on login)
//   2. locale auto-detect
export function getHomeCurrency(): string {
  try {
    const saved = localStorage.getItem(HOME_CCY_KEY);
    if (saved && CURRENCIES[saved]) return saved;
  } catch {}
  return detectCurrency();
}

export function setHomeCurrency(code: string): void {
  try {
    if (CURRENCIES[code]) localStorage.setItem(HOME_CCY_KEY, code);
  } catch {}
}

// Fetch live VND→* rates once per session; falls back to FALLBACK_RATES.
export async function fetchRates(): Promise<Record<string, number>> {
  try {
    const cached = sessionStorage.getItem('hl_fx_rates');
    if (cached) return { ...FALLBACK_RATES, ...JSON.parse(cached) };
  } catch {}
  try {
    const r = await fetch('https://api.exchangerate-api.com/v4/latest/VND');
    const data = await r.json();
    if (data && data.rates) {
      const live: Record<string, number> = { VND: 1 };
      for (const code of CURRENCY_CODES) {
        if (data.rates[code]) live[code] = data.rates[code];
      }
      try { sessionStorage.setItem('hl_fx_rates', JSON.stringify(live)); } catch {}
      return { ...FALLBACK_RATES, ...live };
    }
  } catch {}
  return { ...FALLBACK_RATES };
}

export function convertVnd(vnd: number, currency: string, rates?: Record<string, number>): number {
  const rate = (rates && rates[currency]) || FALLBACK_RATES[currency] || 1;
  return vnd * rate;
}

// Format an amount that is ALREADY in `currency` units.
export function formatMoney(amount: number, currency: string): string {
  const meta = CURRENCIES[currency] || CURRENCIES.VND;
  const n = amount.toLocaleString(dtLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: meta.decimals,
  });
  // Symbol-prefix style (matches the menu). VND uses a suffix ₫ conventionally,
  // but the app uses the prefix symbol everywhere for consistency.
  return `${meta.symbol}${n}`;
}

// Format a VND amount in VND.
export function formatVnd(vnd: number): string {
  return formatMoney(vnd, 'VND');
}

// "₫500,000 ($20)" — VND primary with the home-currency equivalent in brackets.
// If home === VND (or unset), returns just the VND string (no redundant bracket).
export function formatVndWithHome(
  vnd: number,
  homeCurrency: string,
  rates?: Record<string, number>,
): string {
  const primary = formatVnd(vnd);
  if (!homeCurrency || homeCurrency === 'VND') return primary;
  const converted = convertVnd(vnd, homeCurrency, rates);
  return `${primary} (${formatMoney(converted, homeCurrency)})`;
}
